/**
 * Particle sprite textures.
 *
 * Every preset draws a small pre-rendered texture with `drawImage` (far cheaper
 * than per-particle path fills in the rAF loop). Textures are generated once at
 * module init onto offscreen canvases, tinted white so the engine can multiply
 * a per-preset tint over them cheaply — but if a real art asset exists at
 * `src/assets/fx/<name>.webp` the loader prefers it (generated FX art lands
 * there later with no code change, thanks to Vite's `import.meta.glob`).
 */

export type SpriteName =
  | 'dot' // soft radial glow — embers, motes, dust, sparks, fireflies
  | 'wisp' // elongated soft streak — arcana trails, gold bursts
  | 'fog' // large irregular soft puff — fog / smoke
  | 'flake' // small hard-ish speck — ash
  | 'leaf' // laurel-leaf silhouette
  | 'feather'; // raven-feather silhouette

/** A drawable sprite: a canvas (or image) plus its natural size. */
export interface Sprite {
  source: CanvasImageSource;
  width: number;
  height: number;
}

// ── WebP override loader ──────────────────────────────────────────────────────
// Lazy globs so absent files cost nothing. Keys look like
// '../../assets/fx/dot.webp'. When a matching file exists we load it into an
// <img> and swap it in over the procedural fallback once decoded.
const webpModules = import.meta.glob<string>('../../assets/fx/*.webp', {
  eager: false,
  import: 'default',
  query: '?url',
});

function webpKeyFor(name: SpriteName): string | undefined {
  return Object.keys(webpModules).find((k) => k.endsWith(`/${name}.webp`));
}

// ── Procedural generation ─────────────────────────────────────────────────────

function makeCanvas(size: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  return { canvas, ctx };
}

/** Soft radial dot — the workhorse glow sprite. */
function drawDot(size = 64): HTMLCanvasElement {
  const { canvas, ctx } = makeCanvas(size);
  const r = size / 2;
  const g = ctx.createRadialGradient(r, r, 0, r, r, r);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.75)');
  g.addColorStop(0.7, 'rgba(255,255,255,0.2)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return canvas;
}

/** Elongated vertical wisp — a soft streak, taller than wide. */
function drawWisp(w = 32, h = 96): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, h / 2);
  g.addColorStop(0, 'rgba(255,255,255,0.95)');
  g.addColorStop(0.4, 'rgba(255,255,255,0.4)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.save();
  ctx.translate(w / 2, h / 2);
  ctx.scale(0.42, 1); // squash horizontally into a streak
  ctx.translate(-w / 2, -h / 2);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
  return canvas;
}

/** Irregular soft puff — several overlapping blobs for a cloudy silhouette. */
function drawFog(size = 128): HTMLCanvasElement {
  const { canvas, ctx } = makeCanvas(size);
  const blobs = [
    [0.5, 0.5, 0.5],
    [0.36, 0.44, 0.34],
    [0.64, 0.46, 0.32],
    [0.5, 0.62, 0.3],
    [0.44, 0.38, 0.26],
  ] as const;
  for (const [cx, cy, rad] of blobs) {
    const x = cx * size;
    const y = cy * size;
    const r = rad * size;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, 'rgba(255,255,255,0.16)');
    g.addColorStop(0.6, 'rgba(255,255,255,0.06)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  return canvas;
}

/** Small ash flake — a soft-edged speck with a slightly harder core. */
function drawFlake(size = 24): HTMLCanvasElement {
  const { canvas, ctx } = makeCanvas(size);
  const r = size / 2;
  const g = ctx.createRadialGradient(r, r, 0, r, r, r);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.5, 'rgba(255,255,255,0.6)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.ellipse(r, r, r * 0.7, r * 0.5, 0, 0, Math.PI * 2);
  ctx.fill();
  return canvas;
}

/** Laurel-leaf silhouette via a simple two-bezier almond shape + midrib. */
function drawLeaf(w = 40, h = 64): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = 'rgba(255,255,255,1)';
  ctx.beginPath();
  ctx.moveTo(w / 2, 2);
  ctx.quadraticCurveTo(w - 2, h * 0.42, w / 2, h - 2);
  ctx.quadraticCurveTo(2, h * 0.42, w / 2, 2);
  ctx.fill();
  // Midrib for a touch of definition.
  ctx.strokeStyle = 'rgba(0,0,0,0.18)';
  ctx.lineWidth = Math.max(1, w * 0.04);
  ctx.beginPath();
  ctx.moveTo(w / 2, 4);
  ctx.lineTo(w / 2, h - 4);
  ctx.stroke();
  return canvas;
}

/** Raven-feather silhouette — a slimmer, curved almond with a quill tip. */
function drawFeather(w = 32, h = 72): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = 'rgba(255,255,255,1)';
  ctx.beginPath();
  ctx.moveTo(w / 2, 1);
  ctx.quadraticCurveTo(w - 1, h * 0.5, w * 0.55, h - 2);
  ctx.quadraticCurveTo(w / 2, h - 1, w * 0.45, h - 2);
  ctx.quadraticCurveTo(1, h * 0.5, w / 2, 1);
  ctx.fill();
  // Quill.
  ctx.strokeStyle = 'rgba(0,0,0,0.22)';
  ctx.lineWidth = Math.max(1, w * 0.05);
  ctx.beginPath();
  ctx.moveTo(w / 2, 3);
  ctx.lineTo(w * 0.5, h - 3);
  ctx.stroke();
  return canvas;
}

const generators: Record<SpriteName, () => HTMLCanvasElement> = {
  dot: () => drawDot(),
  wisp: () => drawWisp(),
  fog: () => drawFog(),
  flake: () => drawFlake(),
  leaf: () => drawLeaf(),
  feather: () => drawFeather(),
};

// ── Cache + loader ────────────────────────────────────────────────────────────

const cache = new Map<SpriteName, Sprite>();

/**
 * Get a sprite by name. Returns immediately with the procedural texture; if a
 * `<name>.webp` override exists it is fetched in the background and swapped into
 * the same `Sprite` object once decoded, so already-running engines pick it up
 * on their next frame with no re-wiring.
 */
export function getSprite(name: SpriteName): Sprite {
  const existing = cache.get(name);
  if (existing) return existing;

  const canvas = generators[name]();
  const sprite: Sprite = { source: canvas, width: canvas.width, height: canvas.height };
  cache.set(name, sprite);

  const key = webpKeyFor(name);
  if (key) {
    webpModules[key]!()
      .then((url) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => {
          sprite.source = img;
          sprite.width = img.naturalWidth || sprite.width;
          sprite.height = img.naturalHeight || sprite.height;
        };
        img.src = url;
      })
      .catch(() => {
        /* keep procedural fallback */
      });
  }

  return sprite;
}
